import { createSelector } from 'reselect';

import { HOST_PLATFORMS } from 'store/host-info/consts';
import { HostInfoState } from 'store/host-info/types';
import { RootState } from 'store/reducer';
import { initialState } from 'store/host-info/reducer';

/*
 * Tolerant of a state that predates this slice. Specs build partial `mock<RootState>` fixtures, and
 * a dozen of them were written before this existed -- falling back to the initial state keeps every
 * one of them reading "host unknown" rather than throwing on a missing key.
 */
export const hostInfoSelector = (state: RootState): HostInfoState => state.hostInfo ?? initialState;

export const selectHostPlatform = createSelector([hostInfoSelector], hostInfo => hostInfo.platform);

export const selectIsHostInfoSettled = createSelector([hostInfoSelector], hostInfo => hostInfo.isSettled);

/**
 * Whether the tenant portal is framing this app.
 *
 * Gated on the tenant portal SPECIFICALLY rather than on "some desktop host". The portal draws its
 * own chrome around the frame -- a breadcrumb that goes back -- so this app must not draw a second
 * one. tenant-web frames the same pages with no chrome of its own and still needs ours.
 */
export const selectIsTenantPortalHost = createSelector(
  [selectHostPlatform],
  platform => platform === HOST_PLATFORMS.TENANT_PORTAL,
);

/**
 * Whether the NATIVE APP is framing this app -- the one host that draws a header of its own above
 * the frame, and therefore the one host whose pages must not draw a second one.
 *
 * ⭐ THIS IS THE QUESTION VIEWPORT WIDTH WAS BEING ASKED TO ANSWER, AND COULD NOT. A narrow window
 * is not a native shell: the tenant portal is meant to work at small widths and in mobile browsers,
 * and tenant-web is a host with NO chrome at all that can be any width you like. Keying chrome on
 * `width <= 768` took the title, the "View all bookings" control and the back button away from both
 * of them and handed the way out to a native header that is not there.
 */
export const selectIsNativeAppHost = createSelector([hostInfoSelector], hostInfo => hostInfo.isNativeApp);
